<template>
  <nav :class="['w-full flex flex-col gap-1 text-left', { collapsed }]">
    <div v-for="item in items" :key="item.text" class="w-full">
      <!-- Grupo con submenú -->
      <template v-if="item.subItems">
        <div :class="['group-row', { 'group-active': isActive(item) }]">
          <button
            type="button"
            class="group-label"
            :aria-expanded="isExpanded(item)"
            :aria-controls="submenuId(item)"
            :aria-label="collapsed ? item.text : null"
            :title="collapsed ? item.text : null"
            @click="handleItemClick(item)"
          >
            <img class="icon" :src="item.icon" alt="" />
            <span v-if="!collapsed" class="truncate">{{ item.text }}</span>
            <!-- Contraído: chevron pequeño junto al icono para indicar que es un grupo -->
            <svg
              v-else
              :class="['chevron chevron-sm', { open: isExpanded(item) }]"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7.5 5l5 5-5 5" />
            </svg>
          </button>
          <button
            v-if="!collapsed"
            type="button"
            class="toggle-btn"
            :aria-expanded="isExpanded(item)"
            :aria-controls="submenuId(item)"
            :aria-label="`${isExpanded(item) ? 'Collapse' : 'Expand'} ${item.text}`"
            :title="isExpanded(item) ? 'Collapse' : 'Expand'"
            @click="handleItemClick(item)"
          >
            <svg
              :class="['chevron', { open: isExpanded(item) }]"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7.5 5l5 5-5 5" />
            </svg>
          </button>
        </div>

        <div
          :id="submenuId(item)"
          :class="['submenu', { open: isExpanded(item) }]"
          :inert="!isExpanded(item) || null"
        >
          <div class="submenu-inner">
            <button
              v-for="subItem in item.subItems"
              :key="subItem.text"
              type="button"
              :class="['nav-item sub-item', { selected: isActive(subItem) }]"
              :aria-label="collapsed ? subItem.text : null"
              :title="collapsed ? subItem.text : null"
              @click="navigate(subItem)"
            >
              <img class="icon" :src="subItem.icon" alt="" />
              <span v-if="!collapsed" class="truncate">{{ subItem.text }}</span>
            </button>
          </div>
        </div>
      </template>

      <!-- Ítem simple -->
      <button
        v-else
        type="button"
        :class="['nav-item', { selected: isActive(item) }]"
        :aria-label="collapsed ? item.text : null"
        :title="collapsed ? item.text : null"
        @click="handleItemClick(item)"
      >
        <img class="icon" :src="item.icon" alt="" />
        <span v-if="!collapsed" class="truncate">{{ item.text }}</span>
      </button>
    </div>
  </nav>
</template>

<script>
import { useNavMenu } from "@/composables/useNavMenu";

// Lista de ítems del menú lateral, compartida por SideBar (escritorio) y NavDrawer (móvil).
export default {
  props: {
    // Modo "solo iconos" del sidebar de escritorio.
    collapsed: { type: Boolean, default: false },
  },
  emits: ["navigate"],
  setup(_, { emit }) {
    const submenuId = (item) =>
      `submenu-${item.text.toLowerCase().replace(/\s+/g, "-")}`;

    return {
      ...useNavMenu({ onNavigate: (item) => emit("navigate", item) }),
      submenuId,
    };
  },
};
</script>

<style scoped>
.nav-item,
.group-row {
  width: 100%;
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  line-height: 1.375rem;
  transition: background-color 0.2s;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px;
}

.nav-item:hover,
.group-row:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.nav-item.selected {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

/* Fila del grupo: etiqueta + botón de expandir/colapsar */
.group-row {
  display: flex;
  align-items: center;
}

.group-active {
  font-weight: 600;
}

.group-label {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px;
  text-align: left;
}

.toggle-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  transition: background-color 0.2s, color 0.2s;
}

.toggle-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.toggle-btn:focus-visible,
.group-label:focus-visible,
.nav-item:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.7);
  outline-offset: -2px;
}

.chevron {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.chevron.open {
  transform: rotate(90deg);
}

/* Submenú con animación de altura */
.submenu {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.submenu.open {
  grid-template-rows: 1fr;
}

.submenu-inner {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 17px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
}

.submenu.open .submenu-inner {
  margin-top: 4px;
}

.sub-item {
  padding: 7px 9px;
}

.icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* ---- Modo contraído (solo iconos) ---- */
.collapsed .nav-item,
.collapsed .group-label {
  justify-content: center;
  gap: 2px;
}

.collapsed .submenu-inner {
  margin-left: 0;
  padding-left: 0;
  border-left: none;
}

/* Solo con el grupo abierto, para que el borde no asome cuando está cerrado */
.collapsed .submenu.open .submenu-inner {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 4px 0;
}

.chevron-sm {
  width: 10px;
  height: 10px;
  opacity: 0.8;
}

@media (prefers-reduced-motion: reduce) {
  .submenu,
  .chevron {
    transition: none;
  }
}
</style>
